import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useConfirm, useDiscardGuard } from '@/components/ui/confirm-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  CheckCircle2,
  XCircle,
  Plus,
  Pencil,
  Trash2,
  Users,
  LayoutGrid,
  Settings,
  ChevronDown,
  ChevronRight,
  Layers,
} from 'lucide-react';
import {
  clientRecurringTasksService,
  type CreateRecurringTaskDto,
  type UpdateRecurringTaskDto,
  type ClientRecurringTask,
  type RecurringTemplateGroup,
  type RecurringTemplateItemLine,
  type CreateTemplateGroupDto,
  type UpdateTemplateGroupDto,
  type CreateTemplateItemDto,
  type UpdateTemplateItemDto,
} from '@/features/clients/services/client-recurring-tasks.service';
import { formatRecurringDeliveryLabel } from '@/features/clients/utils/recurring-task-delivery';
import { formatRecurringTaskTitleForReferenceMonth } from '@/features/clients/utils/recurring-task-title-display';
import {
  DEFAULT_RECURRING_DELIVERY_DAY,
  getRecurringTaskClientDisplayName,
  sortRecurringTasksByDeliveryDay,
} from '@/features/clients/utils/recurring-task-order';
import { clientsService } from '@/features/clients/services/clients.service';
import { taskTagsService } from '@/features/daily-tasks/services/task-tags.service';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export const Route = createFileRoute('/_authenticated/admin/recurring-client-tasks')({
  component: RecurringClientTasksAdminPage,
});

function RecurringClientTasksAdminPage() {
  return (
    <PermissionGate permission={Permission.RECURRING_CLIENT_TASKS_ADMIN}>
      <RecurringClientTasksAdminContent />
    </PermissionGate>
  );
}

function parseDeliveryDayInput(raw: string): number {
  const digits = raw.replace(/\D/g, '');
  const n = Number.parseInt(digits, 10);
  if (!Number.isFinite(n)) return 10;
  return Math.min(31, Math.max(1, n));
}

/** Nome (dados profissionais) e email separados para exibir em linhas distintas. */
function getUserDisplayParts(user: { email: string; fullName?: string | null }): {
  name: string | null;
  email: string;
} {
  const raw = typeof user.fullName === 'string' ? user.fullName.trim() : '';
  return { name: raw || null, email: user.email };
}

function formatMonthDisplay(month: string) {
  try {
    return format(new Date(month + '-01'), 'MMMM yyyy', { locale: ptBR });
  } catch {
    return month;
  }
}

function currentYyyyMm(): string {
  return new Date().toISOString().slice(0, 7);
}

function MonthPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Label className="shrink-0 text-sm">Mês:</Label>
      <input
        type="month"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-48 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring relative z-[1]"
      />
    </div>
  );
}

// ── Aba 1: Visão Geral ──────────────────────────────────────────────────────

function OverviewTab() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));

  const { data: overview = [], isLoading } = useQuery({
    queryKey: ['recurring-tasks-admin-overview', month],
    queryFn: () => clientRecurringTasksService.getAdminOverview(month),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <MonthPicker value={month} onChange={setMonth} />
        <span className="text-sm text-muted-foreground">
          {formatMonthDisplay(month)}
        </span>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground py-8 text-center">Carregando...</div>
      ) : overview.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">
          <Users className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p>Nenhum responsável com tarefas recorrentes configuradas.</p>
          <p className="text-xs mt-1">Configure tarefas na aba Configuração.</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {overview.map((userGroup) => {
            const completed = userGroup.tasks.filter((t) => t.isCompleted).length;
            const total = userGroup.tasks.length;
            const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
            const { name: userName, email: userEmail } = getUserDisplayParts(userGroup.user);

            return (
              <Card key={userGroup.user.id} className="overflow-hidden">
                <CardHeader className="pb-2 pt-4 px-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      {userName ? (
                        <>
                          <p className="font-semibold text-sm break-words leading-snug">{userName}</p>
                          <p className="text-xs text-muted-foreground break-all mt-0.5">{userEmail}</p>
                        </>
                      ) : (
                        <p className="font-semibold text-sm break-all">{userEmail}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {completed}/{total} concluídas · {pct}%
                      </p>
                    </div>
                    <div
                      className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                        pct === 100
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                          : pct >= 50
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      }`}
                    >
                      {pct}%
                    </div>
                  </div>
                  <div className="w-full bg-muted rounded-full h-1.5 mt-2">
                    <div
                      className={`h-1.5 rounded-full transition-all ${
                        pct === 100 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-4 space-y-1.5">
                  {sortRecurringTasksByDeliveryDay(userGroup.tasks).map((task) => (
                    <div
                      key={task.id}
                      className={`flex items-start gap-2 p-2 rounded-md text-sm ${
                        task.isCompleted
                          ? 'bg-emerald-50 dark:bg-emerald-950/20'
                          : 'bg-muted/40'
                      }`}
                    >
                      {task.isCompleted ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                      ) : (
                        <XCircle className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium break-words leading-snug ${task.isCompleted ? 'line-through text-muted-foreground' : ''}`}>
                          {task.tag?.icon ? `${task.tag.icon} ` : ''}
                          {formatRecurringTaskTitleForReferenceMonth(task.title, month)}
                        </p>
                        <p className="inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold bg-primary/10 text-primary border border-primary/25 w-fit max-w-full break-words">
                          {getRecurringTaskClientDisplayName(task as any)}
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          Entrega:{' '}
                          <span className="font-medium text-foreground">
                            {formatRecurringDeliveryLabel(
                              month,
                              task.deliveryDayOfMonth ?? DEFAULT_RECURRING_DELIVERY_DAY,
                            )}
                          </span>
                        </p>
                        {task.tag && (
                          <Badge
                            variant="outline"
                            className="text-[10px] px-1 py-0 mt-0.5"
                            style={{ borderColor: task.tag.color, color: task.tag.color }}
                          >
                            {task.tag.name}
                          </Badge>
                        )}
                        {task.isCompleted && task.completion && (
                          <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-0.5">
                            {format(new Date(task.completion.completedAt), "dd/MM 'às' HH:mm", { locale: ptBR })}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Aba 2: Por Cliente ──────────────────────────────────────────────────────

function ByClientTab() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());

  const { data: byClient = [], isLoading } = useQuery({
    queryKey: ['recurring-tasks-admin-by-client', month],
    queryFn: () => clientRecurringTasksService.getAdminByClient(month),
  });

  const toggleClient = (clientId: string) => {
    setExpandedClients((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) {
        next.delete(clientId);
      } else {
        next.add(clientId);
      }
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <MonthPicker value={month} onChange={setMonth} />
        <span className="text-sm text-muted-foreground">
          {formatMonthDisplay(month)}
        </span>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground py-8 text-center">Carregando...</div>
      ) : byClient.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">
          <LayoutGrid className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p>Nenhum cliente com tarefas recorrentes configuradas.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {byClient.map((clientGroup) => {
            const isExpanded = expandedClients.has(clientGroup.client.id);
            const allTasks = sortRecurringTasksByDeliveryDay(clientGroup.tasks);
            const totalCompletions = allTasks.reduce((acc, t) => {
              const done = t.attendants.filter((a) => a.isCompleted).length;
              return acc + done;
            }, 0);
            const totalExpected = allTasks.reduce(
              (acc, t) => acc + t.attendants.length,
              0,
            );

            return (
              <Card key={clientGroup.client.id}>
                <button
                  type="button"
                  className="w-full text-left"
                  onClick={() => toggleClient(clientGroup.client.id)}
                >
                  <CardHeader className="pb-3 pt-4 px-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                        )}
                        <div>
                          <CardTitle className="text-base font-bold tracking-tight text-primary">
                            {clientGroup.client.tradeName ?? clientGroup.client.name}
                          </CardTitle>
                          {clientGroup.client.tradeName && (
                            <p className="text-xs text-muted-foreground">{clientGroup.client.name}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span>{allTasks.length} tarefas</span>
                        <span>
                          {totalCompletions}/{totalExpected} conclusões
                        </span>
                        {allTasks.every((t) => t.allCompleted) && allTasks.length > 0 && (
                          <Badge className="bg-emerald-500 text-white text-xs">100%</Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                </button>

                {isExpanded && (
                  <CardContent className="px-4 pb-4 space-y-3 border-t pt-3">
                    {allTasks.map((task) => (
                      <div key={task.id} className="space-y-1.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-medium break-words">
                            {task.tag?.icon ? `${task.tag.icon} ` : ''}
                            {formatRecurringTaskTitleForReferenceMonth(task.title, month)}
                          </p>
                          {task.tag && (
                            <Badge
                              variant="outline"
                              className="text-[10px] px-1.5 py-0"
                              style={{ borderColor: task.tag.color, color: task.tag.color }}
                            >
                              {task.tag.name}
                            </Badge>
                          )}
                          {task.allCompleted && (
                            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground pl-0.5">
                          Entrega:{' '}
                          <span className="font-medium text-foreground">
                            {formatRecurringDeliveryLabel(
                              month,
                              task.deliveryDayOfMonth ?? DEFAULT_RECURRING_DELIVERY_DAY,
                            )}
                          </span>
                        </p>
                        <div className="flex flex-wrap gap-2 pl-2">
                          {task.attendants.map((att) => {
                            const { name: attName, email: attEmail } = getUserDisplayParts(att.user);
                            return (
                            <div
                              key={att.user.id}
                              className={`flex items-start gap-1.5 text-xs px-2 py-1.5 rounded-md border max-w-[min(100%,260px)] ${
                                att.isCompleted
                                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/20 dark:border-emerald-800 dark:text-emerald-400'
                                  : 'bg-muted border-muted-foreground/20 text-muted-foreground'
                              }`}
                            >
                              {att.isCompleted ? (
                                <CheckCircle2 className="h-3 w-3 shrink-0 mt-0.5" />
                              ) : (
                                <XCircle className="h-3 w-3 shrink-0 mt-0.5" />
                              )}
                              <div className="min-w-0 flex-1 flex flex-col gap-0.5">
                                {attName ? (
                                  <>
                                    <span className="font-medium leading-tight break-words">{attName}</span>
                                    <span className="text-[11px] break-all opacity-90">{attEmail}</span>
                                  </>
                                ) : (
                                  <span className="break-all">{attEmail}</span>
                                )}
                              </div>
                              {att.isCompleted && att.completion && (
                                <span className="text-[10px] opacity-70 shrink-0 self-center">
                                  {format(new Date(att.completion.completedAt), 'dd/MM', { locale: ptBR })}
                                </span>
                              )}
                            </div>
                          );
                          })}
                          {task.attendants.length === 0 && (
                            <span className="text-xs text-muted-foreground">
                              Nenhum atendente vinculado
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Aba 3: Biblioteca de modelos ──────────────────────────────────────────

function GroupForm({
  form,
  setForm,
}: {
  form: { name: string; description: string };
  setForm: (v: any) => void;
}) {
  return (
    <div className="space-y-3 py-2">
      <div className="space-y-1.5">
        <Label>Nome do pacote *</Label>
        <Input
          value={form.name}
          onChange={(e) => setForm((p: any) => ({ ...p, name: e.target.value }))}
          placeholder="Ex.: Obrigações mensais — contábil"
        />
      </div>
      <div className="space-y-1.5">
        <Label>Descrição</Label>
        <Textarea
          value={form.description}
          onChange={(e) => setForm((p: any) => ({ ...p, description: e.target.value }))}
          placeholder="Descrição do pacote"
          rows={2}
        />
      </div>
    </div>
  );
}

function TemplatesTab() {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [editGroup, setEditGroup] = useState<RecurringTemplateGroup | null>(null);
  const [groupForm, setGroupForm] = useState({ name: '', description: '' });
  const [addItemGroupId, setAddItemGroupId] = useState<string | null>(null);
  const [editItemCtx, setEditItemCtx] = useState<{
    group: RecurringTemplateGroup;
    item: RecurringTemplateItemLine;
  } | null>(null);
  const [itemForm, setItemForm] = useState({
    title: '',
    description: '',
    tagId: '',
    deliveryDayOfMonth: '10',
  });

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ['recurring-template-groups'],
    queryFn: () => clientRecurringTasksService.listTemplateGroups(),
  });

  const { data: tags = [] } = useQuery({
    queryKey: ['task-tags'],
    queryFn: () => taskTagsService.getAll(),
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['recurring-template-groups'] });
    queryClient.invalidateQueries({ queryKey: ['recurring-client-tasks-my'] });
    queryClient.invalidateQueries({ queryKey: ['recurring-tasks-by-client'] });
    queryClient.invalidateQueries({ queryKey: ['recurring-tasks-admin-overview'] });
    queryClient.invalidateQueries({ queryKey: ['recurring-tasks-admin-by-client'] });
  };

  const createGroupMutation = useMutation({
    mutationFn: (dto: CreateTemplateGroupDto) =>
      clientRecurringTasksService.createTemplateGroup(dto),
    onSuccess: () => {
      invalidateAll();
      setCreateGroupOpen(false);
      setGroupForm({ name: '', description: '' });
      toast.success('Pacote criado');
    },
  });

  const updateGroupMutation = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateTemplateGroupDto }) =>
      clientRecurringTasksService.updateTemplateGroup(id, dto),
    onSuccess: () => {
      invalidateAll();
      setEditGroup(null);
      setGroupForm({ name: '', description: '' });
      toast.success('Pacote atualizado');
    },
  });

  const deleteGroupMutation = useMutation({
    mutationFn: (id: string) => clientRecurringTasksService.removeTemplateGroup(id),
    onSuccess: () => {
      invalidateAll();
      toast.success('Pacote removido');
    },
  });

  const createItemMutation = useMutation({
    mutationFn: (p: { groupId: string; dto: CreateTemplateItemDto }) =>
      clientRecurringTasksService.createTemplateItem(p.groupId, p.dto),
    onSuccess: (data) => {
      invalidateAll();
      setAddItemGroupId(null);
      setItemForm({ title: '', description: '', tagId: '', deliveryDayOfMonth: '10' });
      const n = data.propagated?.tasksCreated ?? 0;
      if (n > 0) {
        toast.success(
          `Tarefa adicionada ao pacote e criada automaticamente em ${n} cliente(s) que já usam este pacote.`,
        );
      } else {
        toast.success('Tarefa adicionada ao pacote');
      }
    },
  });

  const updateItemMutation = useMutation({
    mutationFn: (p: { itemId: string; dto: UpdateTemplateItemDto }) =>
      clientRecurringTasksService.updateTemplateItem(p.itemId, p.dto),
    onSuccess: () => {
      invalidateAll();
      setEditItemCtx(null);
      setItemForm({ title: '', description: '', tagId: '', deliveryDayOfMonth: '10' });
      toast.success('Tarefa do modelo atualizada e aplicada às instâncias pendentes');
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: (id: string) => clientRecurringTasksService.removeTemplateItem(id),
    onSuccess: () => {
      invalidateAll();
      toast.success('Linha removida do modelo');
    },
  });

  const toggleGroupActiveMutation = useMutation({
    mutationFn: (p: { id: string; isActive: boolean }) =>
      clientRecurringTasksService.updateTemplateGroup(p.id, { isActive: p.isActive }),
    onSuccess: () => {
      invalidateAll();
      toast.success('Status do pacote atualizado');
    },
  });

  const toggleItemActiveMutation = useMutation({
    mutationFn: (p: { id: string; isActive: boolean }) =>
      clientRecurringTasksService.updateTemplateItem(p.id, { isActive: p.isActive }),
    onSuccess: () => {
      invalidateAll();
      toast.success('Status da tarefa atualizado');
    },
  });

  const handleCreateGroup = () => {
    if (!groupForm.name.trim()) return;
    createGroupMutation.mutate({
      name: groupForm.name.trim(),
      description: groupForm.description.trim() || undefined,
    });
  };

  const handleUpdateGroup = () => {
    if (!editGroup || !groupForm.name.trim()) return;
    updateGroupMutation.mutate({
      id: editGroup.id,
      dto: {
        name: groupForm.name.trim(),
        description: groupForm.description.trim() || undefined,
      },
    });
  };

  const openEditGroup = (g: RecurringTemplateGroup) => {
    setEditGroup(g);
    setGroupForm({
      name: g.name,
      description: g.description ?? '',
    });
  };

  const handleCreateItem = () => {
    if (!addItemGroupId || !itemForm.title.trim()) return;
    const day = parseDeliveryDayInput(itemForm.deliveryDayOfMonth);
    createItemMutation.mutate({
      groupId: addItemGroupId,
      dto: {
        title: itemForm.title.trim(),
        description: itemForm.description.trim() || undefined,
        deliveryDayOfMonth: day,
        tagId: itemForm.tagId || undefined,
      },
    });
  };

  const handleUpdateItem = () => {
    if (!editItemCtx || !itemForm.title.trim()) return;
    const day = parseDeliveryDayInput(itemForm.deliveryDayOfMonth);
    updateItemMutation.mutate({
      itemId: editItemCtx.item.id,
      dto: {
        title: itemForm.title.trim(),
        description: itemForm.description.trim() || undefined,
        deliveryDayOfMonth: day,
        tagId: itemForm.tagId || null,
        isActive: editItemCtx.item.isActive,
      },
    });
  };

  const openEditItem = (group: RecurringTemplateGroup, item: RecurringTemplateItemLine) => {
    setEditItemCtx({ group, item });
    setItemForm({
      title: item.title,
      description: item.description ?? '',
      tagId: item.tagId ?? '',
      deliveryDayOfMonth: String(item.deliveryDayOfMonth ?? 10),
    });
  };

  const resetItemForm = () =>
    setItemForm({ title: '', description: '', tagId: '', deliveryDayOfMonth: '10' });

  const groupDirty = editGroup
    ? groupForm.name !== editGroup.name || groupForm.description !== (editGroup.description ?? '')
    : !!(groupForm.name.trim() || groupForm.description.trim());
  const itemBase = editItemCtx?.item;
  const itemDirty = itemBase
    ? itemForm.title !== itemBase.title ||
      itemForm.description !== (itemBase.description ?? '') ||
      itemForm.tagId !== (itemBase.tagId ?? '') ||
      itemForm.deliveryDayOfMonth !== String(itemBase.deliveryDayOfMonth ?? 10)
    : !!(itemForm.title.trim() || itemForm.description.trim() || itemForm.tagId) ||
      itemForm.deliveryDayOfMonth !== '10';
  const onCreateGroupOpenChange = useDiscardGuard(groupDirty, setCreateGroupOpen);
  const onEditGroupOpenChange = useDiscardGuard(groupDirty, (o) => {
    if (!o) setEditGroup(null);
  });
  const onAddItemOpenChange = useDiscardGuard(itemDirty, (o) => {
    if (!o) {
      setAddItemGroupId(null);
      resetItemForm();
    }
  });
  const onEditItemOpenChange = useDiscardGuard(itemDirty, (o) => {
    if (!o) setEditItemCtx(null);
  });

  return (
    <div className="space-y-4">
      <div className="rounded-md border bg-muted/30 p-4 text-sm text-muted-foreground">
        <p>
          Cada <strong className="text-foreground">pacote</strong> agrupa várias tarefas recorrentes. Ao editar uma
          tarefa dentro do pacote, o sistema atualiza todas as instâncias nos clientes que seguem essa linha, exceto as
          que já tiverem conclusão no <strong className="text-foreground">mês atual</strong>. Na aba Configuração,
          aplique o pacote inteiro a um cliente de uma vez.
        </p>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={() => {
            setGroupForm({ name: '', description: '' });
            setCreateGroupOpen(true);
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Novo pacote
        </Button>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground py-8 text-center">Carregando biblioteca...</div>
      ) : groups.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground border rounded-lg">
          <Layers className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p>Nenhum pacote na biblioteca.</p>
          <p className="text-xs mt-1">Crie um pacote e adicione as tarefas que se repetem todo mês.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.id} className="border rounded-lg overflow-hidden">
              <div className="flex flex-wrap items-start justify-between gap-3 p-3 bg-muted/20">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold break-words">{g.name}</p>
                    {!g.isActive && (
                      <Badge variant="secondary" className="text-[10px]">
                        Pacote inativo
                      </Badge>
                    )}
                  </div>
                  {g.description ? (
                    <p className="text-xs text-muted-foreground mt-1 break-words">{g.description}</p>
                  ) : null}
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {(g.items ?? []).length} tarefa(s) no modelo
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="text-[10px] h-7"
                    onClick={() =>
                      toggleGroupActiveMutation.mutate({ id: g.id, isActive: !g.isActive })
                    }
                    disabled={toggleGroupActiveMutation.isPending}
                  >
                    {g.isActive ? 'Desativar pacote' : 'Ativar pacote'}
                  </Button>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="text-[10px] h-7"
                      onClick={() => {
                        resetItemForm();
                        setAddItemGroupId(g.id);
                      }}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Tarefa
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" aria-label={`Editar pacote ${g.name}`} onClick={() => openEditGroup(g)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      aria-label={`Remover pacote ${g.name}`}
                      onClick={async () => {
                        if (
                          !(await confirm({
                            title: `Remover o pacote "${g.name}"?`,
                            description:
                              'O pacote e todas as suas linhas serão removidos. Tarefas nos clientes ficam independentes.',
                            confirmLabel: 'Remover pacote',
                            destructive: true,
                          }))
                        )
                          return;
                        deleteGroupMutation.mutate(g.id);
                      }}
                      disabled={deleteGroupMutation.isPending}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="divide-y border-t">
                {(g.items ?? []).length === 0 ? (
                  <p className="text-xs text-muted-foreground p-3">Nenhuma tarefa neste pacote ainda.</p>
                ) : (
                  (g.items ?? []).map((it) => (
                    <div
                      key={it.id}
                      className="flex flex-wrap items-start justify-between gap-2 p-3 text-sm"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium break-words">
                            {it.tag?.icon ? `${it.tag.icon} ` : ''}
                            {formatRecurringTaskTitleForReferenceMonth(it.title, currentYyyyMm())}
                          </span>
                          {!it.isActive && (
                            <Badge variant="outline" className="text-[10px] py-0">
                              Inativa
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Entrega dia {it.deliveryDayOfMonth ?? 10}
                          {it.description ? (
                            <>
                              {' '}
                              · <span className="break-words">{it.description}</span>
                            </>
                          ) : null}
                        </p>
                        {it.tag && (
                          <Badge
                            variant="outline"
                            className="text-[10px] px-1.5 py-0 mt-1"
                            style={{ borderColor: it.tag.color, color: it.tag.color }}
                          >
                            {it.tag.name}
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-[10px] h-7"
                          onClick={() =>
                            toggleItemActiveMutation.mutate({ id: it.id, isActive: !it.isActive })
                          }
                          disabled={toggleItemActiveMutation.isPending}
                        >
                          {it.isActive ? 'Desativar' : 'Ativar'}
                        </Button>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            aria-label={`Editar tarefa ${it.title}`}
                            onClick={() => openEditItem(g, it)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            aria-label={`Remover tarefa ${it.title}`}
                            onClick={async () => {
                              if (
                                !(await confirm({
                                  title: `Remover a tarefa "${it.title}" deste pacote?`,
                                  confirmLabel: 'Remover tarefa',
                                  destructive: true,
                                }))
                              )
                                return;
                              deleteItemMutation.mutate(it.id);
                            }}
                            disabled={deleteItemMutation.isPending}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={createGroupOpen} onOpenChange={onCreateGroupOpenChange}>
        <DialogContent size="md" busy={createGroupMutation.isPending}>
          <DialogHeader>
            <DialogTitle>Novo pacote de modelos</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); handleCreateGroup(); }}>
          <GroupForm form={groupForm} setForm={setGroupForm} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onCreateGroupOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={createGroupMutation.isPending}
              disabled={!groupForm.name.trim()}
            >
              Criar pacote
            </Button>
          </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editGroup} onOpenChange={onEditGroupOpenChange}>
        <DialogContent size="md" busy={updateGroupMutation.isPending}>
          <DialogHeader>
            <DialogTitle>Editar pacote</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); handleUpdateGroup(); }}>
          <GroupForm form={groupForm} setForm={setGroupForm} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onEditGroupOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={updateGroupMutation.isPending}
              disabled={!groupForm.name.trim()}
            >
              Salvar alterações
            </Button>
          </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!addItemGroupId} onOpenChange={onAddItemOpenChange}>
        <DialogContent size="md" busy={createItemMutation.isPending}>
          <DialogHeader>
            <DialogTitle>Nova tarefa no pacote</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); handleCreateItem(); }}>
          <TaskForm form={itemForm} setForm={setItemForm} tags={tags} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onAddItemOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={createItemMutation.isPending}
              disabled={!itemForm.title.trim()}
            >
              Adicionar tarefa
            </Button>
          </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editItemCtx} onOpenChange={onEditItemOpenChange}>
        <DialogContent size="md" busy={updateItemMutation.isPending}>
          <DialogHeader>
            <DialogTitle>Editar tarefa do modelo</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); handleUpdateItem(); }}>
          <TaskForm form={itemForm} setForm={setItemForm} tags={tags} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onEditItemOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={updateItemMutation.isPending}
              disabled={!itemForm.title.trim()}
            >
              Salvar alterações
            </Button>
          </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Aba 4: Configuração ─────────────────────────────────────────────────────

function ConfigTab() {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editTask, setEditTask] = useState<ClientRecurringTask | null>(null);

  const [form, setForm] = useState<{
    title: string;
    description: string;
    tagId: string;
    /** Texto do campo (evita reset para 10 ao apagar dígitos em input type="number"). */
    deliveryDayOfMonth: string;
  }>({
    title: '',
    description: '',
    tagId: '',
    deliveryDayOfMonth: '10',
  });

  const { data: clients = [] } = useQuery({
    queryKey: ['clients-list'],
    queryFn: () => clientsService.listClients({ isActive: true }),
  });

  const { data: tags = [] } = useQuery({
    queryKey: ['task-tags'],
    queryFn: () => taskTagsService.getAll(),
  });

  const { data: recurringTasks = [], isLoading: loadingTasks } = useQuery({
    queryKey: ['recurring-tasks-by-client', selectedClientId],
    queryFn: () => clientRecurringTasksService.listByClient(selectedClientId),
    enabled: !!selectedClientId,
  });

  const { data: templateGroups = [] } = useQuery({
    queryKey: ['recurring-template-groups'],
    queryFn: () => clientRecurringTasksService.listTemplateGroups(),
  });

  const invalidateAllRecurringTaskQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['recurring-client-tasks-my'] });
    queryClient.invalidateQueries({ queryKey: ['recurring-tasks-by-client'] });
    queryClient.invalidateQueries({ queryKey: ['recurring-tasks-admin-overview'] });
    queryClient.invalidateQueries({ queryKey: ['recurring-tasks-admin-by-client'] });
    queryClient.invalidateQueries({ queryKey: ['recurring-template-groups'] });
  };

  const createMutation = useMutation({
    mutationFn: (dto: CreateRecurringTaskDto) => clientRecurringTasksService.create(dto),
    onSuccess: (created) => {
      invalidateAllRecurringTaskQueries();
      queryClient.setQueryData(
        ['recurring-tasks-by-client', selectedClientId],
        (old: ClientRecurringTask[] | undefined) => {
          if (!old) return [created];
          return [...old, created].sort((a, b) => a.title.localeCompare(b.title));
        },
      );
      setCreateOpen(false);
      resetForm();
      toast.success('Tarefa recorrente criada');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateRecurringTaskDto }) =>
      clientRecurringTasksService.update(id, dto),
    onSuccess: (updated) => {
      invalidateAllRecurringTaskQueries();
      queryClient.setQueryData(
        ['recurring-tasks-by-client', selectedClientId],
        (old: ClientRecurringTask[] | undefined) => {
          if (!old) return [updated];
          return old.map((t) => (t.id === updated.id ? { ...t, ...updated } : t));
        },
      );
      setEditTask(null);
      resetForm();
      toast.success('Tarefa atualizada');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => clientRecurringTasksService.remove(id),
    onSuccess: () => {
      invalidateAllRecurringTaskQueries();
      toast.success('Tarefa removida');
    },
  });

  const instantiateFromTemplateMutation = useMutation({
    mutationFn: (templateGroupId: string) =>
      clientRecurringTasksService.instantiateFromTemplate(selectedClientId, templateGroupId),
    onSuccess: (res) => {
      invalidateAllRecurringTaskQueries();
      const created = res.created ?? [];
      queryClient.setQueryData(
        ['recurring-tasks-by-client', selectedClientId],
        (old: ClientRecurringTask[] | undefined) => {
          if (!old) return [...created].sort((a, b) => a.title.localeCompare(b.title));
          const merged = [...old];
          for (const t of created) {
            if (!merged.some((x) => x.id === t.id)) merged.push(t);
          }
          return merged.sort((a, b) => a.title.localeCompare(b.title));
        },
      );
      setSelectedGroupId('');
      const skipped = res.skippedCount ?? 0;
      toast.success(
        skipped > 0
          ? `Pacote aplicado: ${created.length} tarefa(s) criada(s). ${skipped} já existiam e foram ignoradas.`
          : `Pacote aplicado: ${created.length} tarefa(s) criada(s).`,
      );
    },
  });

  const resetForm = () =>
    setForm({ title: '', description: '', tagId: '', deliveryDayOfMonth: '10' });

  const handleCreate = () => {
    if (!form.title.trim() || !selectedClientId) return;
    const day = parseDeliveryDayInput(form.deliveryDayOfMonth);
    createMutation.mutate({
      clientId: selectedClientId,
      title: form.title.trim(),
      description: form.description.trim() || undefined,
      deliveryDayOfMonth: day,
      tagId: form.tagId || undefined,
    });
  };

  const handleUpdate = () => {
    if (!editTask || !form.title.trim()) return;
    const day = parseDeliveryDayInput(form.deliveryDayOfMonth);
    updateMutation.mutate({
      id: editTask.id,
      dto: {
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        deliveryDayOfMonth: day,
        tagId: form.tagId || null,
      },
    });
  };

  const cfgDirty = editTask
    ? form.title !== editTask.title ||
      form.description !== (editTask.description ?? '') ||
      form.tagId !== (editTask.tagId ?? '') ||
      form.deliveryDayOfMonth !== String(editTask.deliveryDayOfMonth ?? 10)
    : !!(form.title.trim() || form.description.trim() || form.tagId) ||
      form.deliveryDayOfMonth !== '10';
  const onCreateTaskOpenChange = useDiscardGuard(cfgDirty, setCreateOpen);
  const onEditTaskOpenChange = useDiscardGuard(cfgDirty, (o) => {
    if (!o) setEditTask(null);
  });

  const openEdit = (task: ClientRecurringTask) => {
    setEditTask(task);
    setForm({
      title: task.title,
      description: task.description ?? '',
      tagId: task.tagId ?? '',
      deliveryDayOfMonth: String(task.deliveryDayOfMonth ?? 10),
    });
  };

  const handleDelete = async (task: ClientRecurringTask) => {
    const ok = await confirm({
      title: `Remover "${task.title}"?`,
      description: 'Esta ação não pode ser desfeita.',
      confirmLabel: 'Remover tarefa',
      destructive: true,
    });
    if (!ok) return;
    deleteMutation.mutate(task.id);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-4 flex-wrap">
        <div className="space-y-1.5">
          <Label>Selecionar cliente</Label>
          <Select value={selectedClientId} onValueChange={setSelectedClientId}>
            <SelectTrigger className="w-72">
              <SelectValue placeholder="Escolha um cliente..." />
            </SelectTrigger>
            <SelectContent>
              {clients.map((client) => (
                <SelectItem key={client.id} value={client.id}>
                  {client.tradeName ?? client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {selectedClientId && (
          <>
            <Button onClick={() => { resetForm(); setCreateOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Nova tarefa recorrente
            </Button>
            {templateGroups.some((g) => g.isActive) && (
              <div className="flex flex-wrap items-end gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">Aplicar pacote da biblioteca</Label>
                  <Select
                    value={selectedGroupId || '__none__'}
                    onValueChange={(v) => setSelectedGroupId(v === '__none__' ? '' : v)}
                  >
                    <SelectTrigger className="w-[min(100vw-2rem,20rem)]">
                      <SelectValue placeholder="Escolha um pacote..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {templateGroups
                        .filter((g) => g.isActive)
                        .map((g) => (
                          <SelectItem key={g.id} value={g.id}>
                            {g.name}
                            {(g.items?.length ?? 0) > 0
                              ? ` (${g.items?.length} tarefa(s))`
                              : ''}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!selectedGroupId || instantiateFromTemplateMutation.isPending}
                  onClick={() =>
                    selectedGroupId && instantiateFromTemplateMutation.mutate(selectedGroupId)
                  }
                >
                  Aplicar ao cliente
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {!selectedClientId ? (
        <div className="py-12 text-center text-muted-foreground">
          <Settings className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p>Selecione um cliente para gerenciar suas tarefas recorrentes.</p>
        </div>
      ) : loadingTasks ? (
        <div className="text-muted-foreground py-4">Carregando tarefas...</div>
      ) : recurringTasks.length === 0 ? (
        <div className="py-8 text-center text-muted-foreground border rounded-lg">
          <p className="text-sm">Nenhuma tarefa recorrente configurada para este cliente.</p>
          <p className="text-xs mt-1">Clique em "Nova tarefa recorrente" para começar.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {recurringTasks.map((task) => (
            <div
              key={task.id}
              className="flex items-start justify-between gap-3 p-3 border rounded-md"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium break-words">
                  {task.tag?.icon ? `${task.tag.icon} ` : ''}
                  {formatRecurringTaskTitleForReferenceMonth(task.title, currentYyyyMm())}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Entrega no dia <span className="font-medium text-foreground">{task.deliveryDayOfMonth ?? 10}</span> de cada mês
                </p>
                {task.description && (
                  <p className="text-xs text-muted-foreground mt-0.5">{task.description}</p>
                )}
                {task.tag && (
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1.5 py-0 mt-1"
                    style={{ borderColor: task.tag.color, color: task.tag.color }}
                  >
                    {task.tag.name}
                  </Badge>
                )}
                {task.templateItemId && (
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 mt-1">
                    Do modelo
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  aria-label={`Editar ${task.title}`}
                  onClick={() => openEdit(task)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  aria-label={`Remover ${task.title}`}
                  onClick={() => handleDelete(task)}
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={onCreateTaskOpenChange}>
        <DialogContent size="md" busy={createMutation.isPending}>
          <DialogHeader>
            <DialogTitle>Nova tarefa recorrente</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); handleCreate(); }}>
          <TaskForm form={form} setForm={setForm} tags={tags} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onCreateTaskOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={createMutation.isPending}
              disabled={!form.title.trim()}
            >
              Criar tarefa
            </Button>
          </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editTask} onOpenChange={onEditTaskOpenChange}>
        <DialogContent size="md" busy={updateMutation.isPending}>
          <DialogHeader>
            <DialogTitle>Editar tarefa recorrente</DialogTitle>
            {editTask?.templateItemId && (
              <p className="text-xs text-muted-foreground font-normal pt-1">
                Esta tarefa está vinculada a uma linha do modelo. Ao salvar alterações no título, descrição, dia ou tag,
                o vínculo com o modelo é removido.
              </p>
            )}
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); handleUpdate(); }}>
          <TaskForm form={form} setForm={setForm} tags={tags} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onEditTaskOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={updateMutation.isPending}
              disabled={!form.title.trim()}
            >
              Salvar alterações
            </Button>
          </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TaskForm({
  form,
  setForm,
  tags,
}: {
  form: {
    title: string;
    description: string;
    tagId: string;
    deliveryDayOfMonth: string;
  };
  setForm: (v: any) => void;
  tags: any[];
}) {
  return (
    <div className="space-y-3 py-2">
      <div className="space-y-1.5">
        <Label>Título *</Label>
        <Input
          value={form.title}
          onChange={(e) => setForm((p: any) => ({ ...p, title: e.target.value }))}
          placeholder="Ex: Relatório mensal"
        />
        <p className="text-[11px] text-muted-foreground leading-snug">
          Dica: use{' '}
          <code className="rounded bg-muted px-1 py-0.5 text-[10px] font-mono">{'{{proximomes}}'}</code> no
          título para que, ao exibir a tarefa, apareça o nome do mês seguinte ao mês de referência (na
          dashboard, o mês do seletor ou o mês do vencimento no calendário).
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
      <div className="space-y-1.5">
        <Label>Dia do mês para entrega *</Label>
        <Input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={2}
          placeholder="1–31"
          value={form.deliveryDayOfMonth}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '').slice(0, 2);
            setForm((p: any) => ({ ...p, deliveryDayOfMonth: digits }));
          }}
          onBlur={() => {
            setForm((p: any) => ({
              ...p,
              deliveryDayOfMonth: String(parseDeliveryDayInput(p.deliveryDayOfMonth)),
            }));
          }}
        />
        <p className="text-[11px] text-muted-foreground">
          Em meses com menos dias (ex.: fevereiro), a data de entrega usa o último dia do mês.
        </p>
      </div>
      <div className="space-y-1.5">
        <Label>Tag</Label>
        <Select
          value={form.tagId || 'none'}
          onValueChange={(v) => setForm((p: any) => ({ ...p, tagId: v === 'none' ? '' : v }))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Sem tag" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Sem tag</SelectItem>
            {tags.map((tag) => (
              <SelectItem key={tag.id} value={tag.id}>
                {tag.icon ? `${tag.icon} ` : ''}{tag.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      </div>
      <div className="space-y-1.5">
        <Label>Descrição</Label>
        <Textarea
          value={form.description}
          onChange={(e) => setForm((p: any) => ({ ...p, description: e.target.value }))}
          placeholder="Descrição..."
          rows={3}
        />
      </div>
    </div>
  );
}

// ── Main Content ────────────────────────────────────────────────────────────

function RecurringClientTasksAdminContent() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Planejamentos Recorrentes de Clientes</h1>
        <p className="text-muted-foreground">
          Gerencie tarefas mensais recorrentes por cliente e acompanhe o progresso dos atendentes.
        </p>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview" className="gap-2">
            <LayoutGrid className="h-4 w-4" />
            Visão Geral
          </TabsTrigger>
          <TabsTrigger value="by-client" className="gap-2">
            <Users className="h-4 w-4" />
            Por Cliente
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-2">
            <Layers className="h-4 w-4" />
            Modelos
          </TabsTrigger>
          <TabsTrigger value="config" className="gap-2">
            <Settings className="h-4 w-4" />
            Configuração
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab />
        </TabsContent>

        <TabsContent value="by-client" className="mt-6">
          <ByClientTab />
        </TabsContent>

        <TabsContent value="templates" className="mt-6">
          <TemplatesTab />
        </TabsContent>

        <TabsContent value="config" className="mt-6">
          <ConfigTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
